import { Button } from '@fountain/fountain-ui-components';
import { CheckCircleOutlineIcon, Icon } from '@fountain/ripple/icon';
import { CircularProgress, TextField, Typography } from '@material-ui/core';
import classNames from 'classnames';
import React, { useState } from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import messages from './messages';
import { useStyles } from './styles';
import { RequestStatus } from './types';

export type Decision = 'approved' | 'rejected';

const REASON_FIELD_ID = 'offer-rejection-reason';

/**
 * Why the viewer has nothing to act on, when they have nothing to act on.
 *
 * Three distinct reasons that say opposite things — collapsing any two tells
 * somebody who is finished that they are waiting, or tells the person who sent
 * the offer that they already decided it.
 */
export type ViewerState =
  /** Every level they were asked, they have answered. */
  | 'responded'
  /** They are on a level that has not been reached yet. */
  | 'waiting'
  /** They submitted it and are not an approver on it. */
  | 'observer'
  /**
   * The request itself is over. Takes precedence over the three above: a
   * rejection leaves later levels `pending`, so a viewer named on one still has
   * an unanswered row and would otherwise read as `waiting`.
   */
  | 'settled';

const VIEWER_STATE_MESSAGES: Record<ViewerState, MessageDescriptor> = {
  responded: messages.alreadyResponded,
  waiting: messages.waitingOnOthers,
  settled: messages.requestSettled,
  observer: messages.observerOnly,
};

interface DecisionFooterProps {
  /**
   * The level being decided. Absent when the viewer has nothing to act on, which
   * is what makes the footer explain itself rather than offer controls.
   */
  stepId?: number;
  /** Why there is nothing to act on. Read only when `stepId` is absent. */
  viewerState: ViewerState;
  submitting: boolean;
  /** What the server said when a decision was refused, verbatim. */
  error?: string;
  onDecide: (stepId: number, decision: Decision, comment?: string) => void;
  /**
   * Which state the footer is describing (Design Requirements §6's matrix).
   *
   * Read for two things `stepId` and `viewerState` cannot answer: whether the
   * edit control reads "Edit offer" or "Revise & resubmit", and whether a
   * settled request gets the "Offer approved" status label rather than the
   * generic already-decided note.
   */
  requestStatus: RequestStatus;
  /**
   * Opens the offer form (AXHE-4759). Absent — and the control with it — when
   * the reader may not edit: no "Can edit offers pending approval" permission,
   * or an offer already delivered to the applicant, which the server refuses to
   * rewrite.
   */
  onEdit?: () => void;
}

/**
 * Approve or reject, in the panel's pinned footer (AXHE-3947).
 *
 * ## Reject is not the primary control
 *
 * Approve is `primary`, Reject is a secondary negative.
 * `constraint-anti-pattern-guard` prohibits a destructive action in the primary
 * visual position, and a rejection here ends the request outright — one
 * rejection at any level short-circuits the whole chain
 * (Approvals::RecordDecision), so it is the most consequential control on the
 * surface and the least prominent by design.
 *
 * ## The reason is mandatory
 *
 * Reject opens a field instead of submitting, and the confirm stays disabled
 * until there is text in it.
 *
 * This gate is client-only, which is worth stating plainly because it looks like
 * a mirror of a server rule and is not: `DecisionsController#create` passes
 * `comment: params[:comment].presence` unchecked, and `RecordDecision#validate!`
 * never reads it. So a direct POST records a rejection with no reason, and the
 * `rejectionReason` a resubmitting recruiter reads comes back empty. Closing that
 * needs a rule in the engine, which is shared with the openings path — filed
 * rather than smuggled in here.
 */
export const DecisionFooter: React.FC<DecisionFooterProps> = ({
  stepId,
  viewerState,
  submitting,
  error,
  onDecide,
  requestStatus,
  onEdit,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  // Whether the reason field is open. Two states rather than one nullable
  // reason, so "rejecting with the field still empty" is representable — that is
  // the state the disabled confirm exists for.
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const canConfirmRejection = reason.trim().length > 0;

  /**
   * The edit control, in whichever group and weight this state gives it
   * (Design Requirements §6's footer matrix).
   *
   * On a REJECTED offer it is "Revise & resubmit", primary, on the right: there
   * is no decision left to make, so revising is the only thing the reader came
   * to do. Everywhere else it is a secondary "Edit offer" on the left, beside
   * — and visually behind — the decision it is an alternative to.
   *
   * A WITHDRAWN request takes the rejected treatment too, though the design's
   * matrix does not name it. It is the same situation to act on — nothing was
   * decided, the offer is back at draft, and submitting it again is the only way
   * forward — and one of its two causes is this very flow: editing an offer
   * that is still under approval cancels the request it was raised against
   * (OfferLetters::UpdatePreview). Leaving it on the secondary control would put
   * the one action that resolves the state behind the sentence describing it.
   *
   * Withheld entirely while a rejection is being written, rather than disabled.
   * The reason field is a focused, single-outcome task, and a third control
   * beside Cancel and Reject offer during it invites an edit that would discard
   * the reason mid-sentence.
   *
   * ## The prohibition this diverges from
   *
   * `constraint-anti-pattern-guard` says a permission-based action is rendered
   * disabled rather than hidden, so a reader can discover an ability to ask for.
   * This one is hidden, which is what the Design Requirements and the ticket
   * both specify, and the panel already carries the compensating affordance: the
   * footer says why there is nothing to act on rather than going blank. Nearly
   * every approver holds no edit permission, so the alternative is a permanently
   * inert button in the footer of every offer they ever review.
   */
  const revising =
    requestStatus === 'rejected' || requestStatus === 'cancelled';
  const editControl =
    onEdit && !rejecting ? (
      <Button
        autoWidth
        disabled={submitting}
        onClick={onEdit}
        type={revising ? 'primary' : 'secondary'}
      >
        <FormattedMessage
          {...(revising ? messages.reviseAndResubmit : messages.editOffer)}
        />
      </Button>
    ) : null;
  // Left when it is the alternative to a decision; right when it IS the action.
  const leftControl = revising ? null : editControl;
  const rightEditControl = revising ? editControl : null;

  if (stepId === undefined) {
    return (
      <div className={styles.footer}>
        <div className={styles.footerGroups}>
          {/* Always rendered, empty when there is no control, so the right-hand
              group keeps its position across states. */}
          <div className={styles.footerGroup}>{leftControl}</div>
          <div className={styles.footerGroup}>
            {/* Two mutually exclusive states: what the request settled at, then
                why the reader has nothing to act on. */}
            {requestStatus === 'approved' ? (
              /* Not a button — the offer is through, and there is nothing left
                 to press. A check and a word, so the state is not carried by
                 color alone. */
              <div className={styles.footerConfirmed}>
                <Icon icon={CheckCircleOutlineIcon} size="lg" color="inherit" />
                <Typography variant="body2" color="inherit">
                  <FormattedMessage {...messages.offerApproved} />
                </Typography>
              </div>
            ) : (
              /* Nothing to act on, and the footer says WHY rather than going
                 blank — an empty footer reads as a surface that failed to load
                 its controls. Suppressed beside Revise & resubmit, which is
                 itself the answer to "what happens now". */
              (rightEditControl ?? (
                <Typography className={styles.footerNote} variant="body2">
                  {/* A withdrawn request is settled, but it was not decided and
                      it is not finished — the generic sentence is false twice
                      over on it. Overridden here rather than by widening
                      `viewerState`, which answers a question about the READER
                      ("have you responded") and not about the request. */}
                  <FormattedMessage
                    {...(viewerState === 'settled' &&
                    requestStatus === 'cancelled'
                      ? messages.requestWithdrawn
                      : VIEWER_STATE_MESSAGES[viewerState])}
                  />
                </Typography>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.footer}>
      {/* Above the buttons on its own full-width row, beside the control it
          explains. Inline it would either clip or push the buttons off the
          right edge. */}
      {error && (
        <Typography className={styles.footerError} variant="body2" role="alert">
          {error}
        </Typography>
      )}

      {rejecting && (
        <TextField
          autoFocus
          className={styles.reasonField}
          // Explicit, because MUI v4 mints no id of its own: without one the
          // `InputLabel` carries no `htmlFor` and the field carries no `id`, so
          // the visible label is not the field's accessible name at all. Static
          // is safe — one panel is open at a time, so one reason field exists.
          id={REASON_FIELD_ID}
          label={intl.formatMessage(messages.rejectReasonLabel)}
          helperText={intl.formatMessage(messages.rejectReasonHelper)}
          multiline
          rows={2}
          value={reason}
          variant="outlined"
          onChange={event => setReason(event.target.value)}
          disabled={submitting}
        />
      )}

      <div className={styles.footerGroups}>
        <div className={styles.footerGroup}>{leftControl}</div>
        <div className={styles.footerGroup}>
          {rejecting ? (
            <>
              <Button
                autoWidth
                type="secondary"
                disabled={submitting}
                onClick={() => {
                  setRejecting(false);
                  setReason('');
                }}
              >
                <FormattedMessage {...globalMessages.cancel} />
              </Button>
              {/* Outlined, not filled. `constraint-anti-pattern-guard` keeps a
                destructive action out of the primary visual position, and this
                is the press that ends the request — one rejection at any level
                short-circuits the whole chain. Cancel keeps the neutral box
                beside it, so the footer carries no filled control at all, which
                is the same shape `RejectOpeningDialog` and `DeleteRuleDialog`
                give their own confirms. */}
              <Button
                autoWidth
                negative
                className={classNames(styles.destructiveButton, {
                  [styles.destructiveSubmitting]: submitting,
                })}
                type="secondary"
                // Named explicitly because the label is swapped for a spinner
                // while the decision is in flight, which would otherwise leave
                // the control with no accessible name at exactly the moment
                // somebody is waiting to hear what happened.
                aria-label={intl.formatMessage(messages.rejectOffer)}
                // The mandatory reason, enforced on the control rather than on
                // submit: an approver should not be able to press this and then
                // be told the reason was required.
                disabled={submitting || !canConfirmRejection}
                onClick={() => onDecide(stepId, 'rejected', reason.trim())}
              >
                {submitting ? (
                  <CircularProgress size={18} />
                ) : (
                  <FormattedMessage {...messages.rejectOffer} />
                )}
              </Button>
            </>
          ) : (
            <>
              {/* Opens the reason field rather than submitting. The two-step is
                what makes the reason mandatory without a modal — and a modal
                here would stack over the panel for a single text field, which
                the guard reserves for single-outcome tasks. */}
              <Button
                autoWidth
                negative
                className={styles.destructiveButton}
                type="secondary"
                disabled={submitting}
                onClick={() => setRejecting(true)}
              >
                <FormattedMessage {...messages.rejectOffer} />
              </Button>
              <Button
                autoWidth
                type="primary"
                aria-label={intl.formatMessage(messages.approveOffer)}
                disabled={submitting}
                onClick={() => onDecide(stepId, 'approved')}
              >
                {submitting ? (
                  <CircularProgress size={18} />
                ) : (
                  <FormattedMessage {...messages.approveOffer} />
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
